// Lines of chat on the page: the speaker's name to click for their
// profile, web links that open outside and cut off from this page, places
// on this grid that open the map, and emotes written as the speaker doing
// something. What the words mean is worked out in `chattext`.

import type { Vec3 } from "@/net/codec";
import { el, openExternal } from "./dom";
import { emoteTail, pieces } from "./chattext";

/** What clicking something in a line of chat does. */
export interface ChatLinks {
  profile(id: string, name: string): void;
  /** A place on this grid: shown on the map, from where it is a click to go. */
  place(regionName: string, position: Vec3): void;
}

/** The words of a line, with its links live. Nothing in it is ever read as markup. */
export function chatText(text: string, links: ChatLinks | null): DocumentFragment {
  const out = document.createDocumentFragment();
  for (const p of pieces(text, location.origin)) {
    if (p.kind === "text") {
      out.append(p.text);
    } else if (p.kind === "url") {
      out.append(
        el("a", {
          class: "chat-link",
          href: p.href,
          target: "_blank",
          rel: "noopener noreferrer",
          title: p.href,
          onClick: (e: Event) => {
            e.preventDefault();
            openExternal(p.href);
          },
        }, p.text),
      );
    } else if (links) {
      out.append(
        el("a", {
          class: "chat-link place",
          href: "#",
          title: `Show ${p.regionName} on the map`,
          onClick: (e: Event) => {
            e.preventDefault();
            links.place(p.regionName, p.position);
          },
        }, p.text),
      );
    } else {
      out.append(p.text);
    }
  }
  return out;
}

/** Who said it: their profile when clicked, if they are somebody and anything can open one. */
export function speaker(name: string, id: string | null | undefined, links: ChatLinks | null): HTMLElement {
  if (!id || !links) return el("span", { class: "who" }, name);
  return el("span", {
    class: "who link",
    title: `${name}'s profile`,
    onClick: (e: Event) => {
      e.stopPropagation();
      links.profile(id, name);
    },
  }, name);
}

/**
 * The speaker and the words of a line, ready to put in one: "Name words"
 * for an emote, read as one sentence, or the name and then what was said.
 * Whether it was an emote comes back too, for the line to be styled as one.
 */
export function chatBody(
  line: { from: string; fromId?: string | null; text: string },
  links: ChatLinks | null,
  withName = true,
): { nodes: Node[]; emote: boolean } {
  const tail = emoteTail(line.text);
  if (tail !== null) {
    return { nodes: [speaker(line.from, line.fromId, links), chatText(tail, links)], emote: true };
  }
  const nodes: Node[] = withName ? [speaker(line.from, line.fromId, links)] : [];
  nodes.push(chatText(line.text, links));
  return { nodes, emote: false };
}

/** How an emote reads as plain text, for places that cannot hold anything else. */
export function plainLine(from: string, text: string): string {
  const tail = emoteTail(text);
  return tail === null ? text : `${from}${tail}`;
}
